<template>
  <div id="uberEats" :class="{ mobile: isMobile }">
    <PageTitle :title="$t('uber_eats_integration.title')" :subtitle="$t('uber_eats_integration.subtitle')">
      <a class="btnWeb btn btn-primary" :href="webUrl" v-if="webUrl" target="_blank">
        {{ $t("uber_eats_integration.go_to_web") }}
        <svgicon src="icon/link.svg" :height="14" :width="14"></svgicon>
      </a>
    </PageTitle>

    <div class="page-container" v-if="ready">
      <Tabs>
        <!-- Empty -->
        <TabSection size="big" v-if="!integrationDone && !isSuperAdmin">
          <Empty :text="$t('uber_eats_integration.empty')"></Empty>
        </TabSection>

        <!-- Integration steps -->
        <TabSection size="big" :title="$t('uber_eats_integration.general_config_title')"
                    :subTitle="$t('uber_eats_integration.general_config_subtitle')" v-if="!integrationDone && isSuperAdmin">
          <div class="card-content">
            <div class="card-section">
              <div class="left">
                <!-- (start->yes) - Choose another company associated to this merchant -->
                <button class="btn btn-primary" v-if="isIntegrationStepVisible('chooseStore')" @click="reset">
                  {{ $t("uber_eats_integration.start_over") }}
                </button>
                <div class="explanation-bloc top-separator" v-if="isIntegrationStepVisible('chooseStore')">
                  <div class="discreet-txt">{{ $t('uber_eats_integration.txt.8') }}</div>
                  <template v-if="integrationState.companies.all">
                    <select v-if="integrationState.companies.all.length" v-model="integrationState.companies.selected" class="full-width">
                      <option v-for="company of integrationState.companies.all" :value="company" :key="`c_${company.companyId}`">
                        {{ company.branchName }} - {{ company.companyName }}
                      </option>
                    </select>
                    <select v-else disabled>
                      <option disabled selected>{{ $t('uber_eats_integration.txt.9') }}</option>
                    </select>
                  </template>
                </div>

                <!-- (start->yes->companyChosen || chooseStore) - Choose the right store among those controlled by this merchant -->
                <div v-if="isIntegrationStepVisible('chooseStore')">
                  <div class="discreet-txt" >{{ $t('uber_eats_integration.txt.11').replace('%COMPANY%', company.name) }}</div>
                  <select v-model="integrationState.userStores.selected" class="full-width">
                    <option :value="null" disabled>{{ $t('uber_eats_integration.txt.12') }}</option>
                    <option v-for="store of integrationState.userStores.all" :value="store" :key="`st_${store.store_id}`">{{ store.name }}</option>
                  </select>
                </div>
              </div>
              <div class="right">
                <!-- (start->yes->companyChosen || chooseStore) - Choose the right store among those controlled by this merchant -->
                <div class="step" v-if="isIntegrationStepVisible('chooseStore')">

                  <!-- Compare data -->
                  <div v-if="uberStoreDataToCompare" class="flex-space-around">
                    <div class="group flex-item-50-percent">
                      <label>{{ $t('uber_eats_integration.ishop_data') }}</label>
                      <JsonViewer :value="companyDataToCompare" expanded copyable boxed :expand-depth="5"/>
                    </div>
                    <div class="group flex-item-50-percent">
                      <label>{{ $t('uber_eats_integration.uber_data') }}</label>
                      <JsonViewer :value="uberStoreDataToCompare" expanded copyable boxed :expand-depth="5"/>
                    </div>
                  </div>

                  <!-- Assign store_id -->
                  <div v-if="integrationState.userStores.selected" class="assign-button-container">
                    <button class="btn btn-primary" @click="assignStoreId(integrationState.userStores.selected.store_id)">
                      {{ $t('uber_eats_integration.assign') }}
                    </button>
                  </div>
                </div>

                <!-- (start->no) Ask the owner to authorize our app to manage their store(s) -->
              </div>
            </div>
          </div>

          <div class="init-step" v-if="isIntegrationStepVisible('initial.no') && authorizationLinksLoaded">
            <p>{{ $t('uber_eats_integration.txt.1') }}</p>
            <ol>
              <li>
                <div class="div-li">
                  <div v-html="$t('uber_eats_integration.txt.init')"></div>
                  <button class="btn btn-primary" @click="initializeUberEatsForInventory">
                    {{ $t('uber_eats_integration.init_inventory') }}
                  </button>
                </div>
              </li>
              <li>
                <div class="div-li">
                  <div v-html="$t('uber_eats_integration.txt.2')"></div>
                  <a class="btn btn-primary" :href="integrationState.authorizationLinks.localhost" target="_blank">
                    {{ $t('uber_eats_integration.get_my_code') }}
                    <svgicon src="icon/link.svg" :height="16" :width="16"></svgicon>
                  </a>
                </div>
              </li>
              <li>
                <div class="div-li">
                  <div>{{ $t('uber_eats_integration.txt.3') }}</div>
                  <textarea class="auth-code" v-model="integrationState.authorizationCode"
                            :placeholder="$t('uber_eats_integration.my_code')"></textarea>
                  <br>
                  <label>{{ $t("uber_eats_integration.txt.6")}}</label>
                </div>
              </li>
              <li>
                <div class="div-li">
                  <div>{{ $t('uber_eats_integration.txt.4') }}</div>
                  <button class="btn btn-primary" @click="consumeAuthorizationCode"
                          :disabled="!integrationState.authorizationCode">
                    {{ $t('uber_eats_integration.consume_code') }}
                  </button>
                </div>
              </li>
            </ol>
          </div>
        </TabSection>

        <!-- General config -->
        <TabSection size="big" :title="$t('uber_eats_integration.general_config_title')"
                    :subTitle="$t('uber_eats_integration.general_config_subtitle')" v-if="integrationDone">
          <div class="card-content">
            <div class="card-section">
              <div class="left">
                <div class="group">
                  <label class="bold-txt">{{ $t('uber_eats_integration.store_id') }}</label>
                  <input type="text" v-model.trim="settings.store_id" disabled>
                </div>
                <!-- Config actions -->
                <div class="buttons">
                  <button class="btn btn-primary" @click="validateAndSave">
                    {{ $t('validate') }} & {{ $t('save') }}
                  </button>
                  <button class="btn btn-primary" @click="restartIntegration" v-if="isSuperAdmin">
                    {{ $t('uber_eats_integration.restart_integration') }}
                  </button>
                </div>
              </div>

              <div class="right">
                <div class="group">
                  <div>
                    <div class="bold-txt">{{ $t('uber_eats_integration.export_locales_title') }}</div>
                    <div class="discreet-txt">{{ $t('uber_eats_integration.export_locales_subtitle') }}</div>
                  </div>
                  <div class="locale-container">
                    <div class="locales" v-for="locale of locales">
                      <input :id="`chk_locale_${locale}`" type="checkbox" :value="locale" v-model="settings.only_export_locales">
                      <label :for="`chk_locale_${locale}`">{{ locale }}</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Store infos -->
            <div class="card-section" v-if="uberStoreData">
              <div class="left padding-top">
                <InformationBlock :title="$t('uber_eats_integration.store_info')" icon="/icon/map.svg"
                                  :data="formattedStoreData"/>
              </div>
              <div class="right padding-top">
                <InformationBlock :title="$t('uber_eats_integration.store_location')" icon="/icon/nav/revenue-center.svg"
                    :data="formattedStoreLocation"/>
              </div>
            </div>
            <!-- Store status -->
            <div class="card-section top-separator">
              <div class="section-header full-width">
                <div class="section-title bold-txt">{{ $t('uber_eats_integration.store_status_title') }}</div>
                <div class="discreet-txt">{{ $t('uber_eats_integration.store_status_subtitle') }}</div>
              </div>
            </div>
            <div class="card-section">
              <div class="left padding-top">
                <InformationBlock :title="$t('uber_eats_integration.integration_status')" icon="/icon/nav/config.svg"
                    :data="formattedPosData"/>
                <div v-if="isSuperAdmin && uberPosData">
                  <button class="btn btn-primary" @click="setPosIntegrationStatus('enable')"
                          v-show="!uberPosData.integration_enabled">
                    {{ $t('uber_eats_integration.enable_integration') }}
                  </button>
                  <button class="btn btn-primary" v-if="isSuperAdmin" @click="setPosIntegrationStatus('disable')"
                          v-show="uberPosData.integration_enabled">
                    {{ $t('uber_eats_integration.disable_integration') }}
                  </button>
                </div>
              </div>
              <div class="right padding-top">
                <div class="current-status" v-if="uberStoreStatusData">
                  <InformationBlock :title="$t('uber_eats_integration.restaurant_status')" icon="/icon/nav/config.svg"
                      :data="formattedUberStoreStatusData"/>
                </div>
                <div class="status-container" v-if="isSuperAdmin">
                  <div class="group">
                    <div class="bold-txt">{{ $t('uber_eats_integration.restaurant_status') }}</div>
                    <div class="discreet-txt">{{ $t('uber_eats_integration.restaurant_status_description') }}</div>
                  </div>
                  <div class="status-option disabled"><!-- If offline, uber decision or oustide opening hours, but only set by uber-->
                    <input id="statusOffline" type="radio" value="OFFLINE" v-model="storeStatusSetter.status" disabled>
                    <label for="statusOffline">{{ $t('uber_eats_integration.offline') }}</label>
                  </div>
                  <div class="status-option">
                    <input id="statusOnline" type="radio" value="ONLINE" v-model="storeStatusSetter.status">
                    <label for="statusOnline">{{ $t('uber_eats_integration.online') }}</label>
                  </div>
                  <div class="status-option">
                    <input id="statusPaused" type="radio" value="PAUSED" v-model="storeStatusSetter.status">
                    <label for="statusPaused">{{ $t('uber_eats_integration.paused') }}</label>
                  </div>
                  <div class="status-change" :class="{ disabled: !hasStoreStatusChangedLocally }">
                    <div class="group">
                      <label>{{ $t('uber_eats_integration.status_change_reason') }}</label>
                      <input type="text" v-model.trim="storeStatusSetter.reason" :disabled="!hasStoreStatusChangedLocally"
                             :placeholder="$t('uber_eats_integration.status_change_reason_desc')">
                    </div>
                    <button class="btn btn-primary" @click="setStoreStatus(storeStatusSetter)"
                            :disabled="!hasStoreStatusChangedLocally">
                      {{ $t('uber_eats_integration.set_status') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabSection>

        <!-- Menu catalog, menu hours and alcohol content-->
        <TabSection size="big" :title="$t('uber_eats_integration.menu_sync_title')"
                    :subTitle="$t('uber_eats_integration.menu_sync_subtitle')" v-if="integrationDone">
          <!-- Menu -->
          <div class="card-content">
            <div class="card-section">
              <div class="left justify-end group">
                <label>{{ $t('uber_eats_integration.menu_selection') }}</label>
                <select v-model="menu.catalog">
                  <option :value="null">---</option>
                  <option :value="c.id" v-for="(c, catIndex) of catalogSelection" :key="`cat_opt_${catIndex}`">{{c.name}}</option>
                </select>
              </div>
              <div class="right menu-name">
                <LocalizedInput title="Name" :translate="menu.name"/>
              </div>
            </div>
            <div class="card-section top-separator">
              <div class="full-width">
                <div class="section-title bold-txt">{{ $t('uber_eats_integration.menu_ordering_hours') }}</div>
                <div class="discreet-txt">{{ $t('uber_eats_integration.menu_ordering_hours_subtitle') }}</div>
              </div>
            </div>
            <div class="card-section padding-top">
              <div class="left catalog-selection group">
                <label>{{ $t('uber_eats_integration.ordering_hours') }}</label>
                <select v-model="menu.hours">
                  <option :value="null">---</option>
                  <option :value="hour.id" v-for="hour of hoursSelection" :key="`hour_opt_${hour.id}`">{{ hour.name }}</option>
                </select>
              </div>
              <div class="right justify-end group">
              </div>
            </div>
            <div class="card-section">
              <div class="left">
                <DayTimeRange v-for="dayName of firstHalfDaysOfTheWeek" :key="`cus_h_${dayName}`"
                              v-if="isCustomHours || ( menuHours && menuHours[dayName] )"
                              :day="(isCustomHours && isEditingCustomHours) ? editedCustomHours[dayName] : menuHours[dayName]"
                              :dayName="dayName" :displayOnly="!isEditingCustomHours"/>
                <div v-if="!menuHours && !isCustomHours" class="no-hours">
                  {{ $t('no_hours') }}
                </div>
              </div>
              <div class="right">
                <DayTimeRange v-for="dayName of secondHalfDaysOfTheWeek" :key="`cus_h_${dayName}`"
                              v-if="isCustomHours || ( menuHours && menuHours[dayName] )"
                              :day="(isCustomHours && isEditingCustomHours) ? editedCustomHours[dayName] : menuHours[dayName]"
                              :dayName="dayName" :displayOnly="!isEditingCustomHours"/>
              </div>
            </div>
            <!-- Alcohol content-->
            <div class="card-section top-separator">
              <div class="full-width padding-bottom">
                <div class="section-title bold-txt">{{ $t('uber_eats_integration.alcohol_content_title') }}</div>
                <div class="discreet-txt">{{ $t('uber_eats_integration.alcohol_content_subtitle') }}</div>
              </div>
            </div>
            <div class="item-property__holder card-section">
              <ItemPropertyEdit :itemPool="companyItems" :selectedItems="getFlatProductsForAlcoholContent"
                                propertyName="alcohol_by_volume" :fieldName="$t('uber_eats_integration.alcohol_by_volume')"
                                :addText="$t('uber_eats_integration.add_alcoholic_product')"
                                @update="updateAlcoholContentArray"/>
            </div>
            <div class="card-section menu-sync-actions">
              <button class="btn btn-secondary menu-sync-action" v-if="isSuperAdmin" @click="fetchUberAndIshopMenus">
                {{ $t('uber_eats_integration.check_uber_data') }}
              </button>
              <button class="btn btn-primary menu-sync-action" @click="saveMenuAndAlcohol">
                {{ $t('uber_eats_integration.save_draft') }}
              </button>
              <button class="btn btn-primary menu-sync-action" @click="saveAndSyncMenus">
                {{ $t('uber_eats_integration.save_uber') }}
              </button>
            </div>
            <div class="card-section top-separator" v-if="uberMenusData && ishopMenusData && isSuperAdmin">
              <div class="uber-data-title">
                <div>
                  <div class="section__title_superadmin">
                    <div class="section-title bold-txt">{{ $t('uber_eats_integration.menus_data') }}</div>
                    <tag>SuperAdmin</tag>
                  </div>
                  <div class="discreet-txt">{{ $t('uber_eats_integration.menus_data_subtitle') }}</div>
                </div>
                <button class="btn btn-secondary" @click="uberMenusData = null">X</button>
              </div>
            </div>
            <div class="card-section card-section__json card-section__json_no_wrap"
                 v-if="uberMenusData && ishopMenusData && isSuperAdmin">
              <div class="jsonViewerContainer">
                <label>{{ $t('ishopfood') }}</label>
                <JsonViewer :value="ishopMenusData" copyable boxed/>
              </div>
              <div class="jsonViewerContainer">
                <label>{{ $t('ubereats') }}</label>
                <JsonViewer :value="uberMenusData" copyable boxed/>
              </div>
            </div>
          </div>
        </TabSection>

        <!-- Holiday Hours -->
        <TabSection size="big" :title="$t('uber_eats_integration.holiday_hours_title')"
                    :subTitle="$t('uber_eats_integration.holiday_hours_subtitle')" v-if="integrationDone">
          <div class="card-content">
            <div class="card-section">
              <HolidayHours :holidayHours="holidayHours"/>
            </div>
            <div class="card-section menu-sync-actions">
              <button class="btn btn-secondary menu-sync-action" v-if="isSuperAdmin" @click="fetchUberHolidays">
                {{ $t('uber_eats_integration.check_uber_data') }}
              </button>
              <button class="btn btn-primary menu-sync-action" @click="saveHolidays">
                {{ $t('uber_eats_integration.save_draft') }}
              </button>
              <button class="btn btn-primary menu-sync-action" @click="saveAndSyncHolidays">
                {{ $t('uber_eats_integration.save_uber') }}
              </button>
            </div>
          </div>
          <div class="card-section top-separator" v-if="uberHolidaysData && isSuperAdmin">
            <div class="uber-data-title padding-bottom">
              <div>
                <div class="section__title_superadmin">
                  <div class="section-title bold-txt">{{ $t('uber_eats_integration.holiday_hours_data') }}</div>
                  <tag>SuperAdmin</tag>
                </div>
                <div class="discreet-txt">{{ $t('uber_eats_integration.holiday_hours_data_subtitle') }}</div>
              </div>
              <button class="btn btn-secondary" @click="uberHolidaysData = null">X</button>
            </div>
          </div>
          <div class="card-section card-section__json" v-if="uberHolidaysData && isSuperAdmin">
            <JsonViewer :value="uberHolidaysData" copyable boxed/>
          </div>
        </TabSection>

        <!-- Debug -->
        <TabSection size="big" :title="$t('uber_eats_integration.debug_title')" :subTitle="$t('uber_eats_integration.debug_subtitle')"
                    v-if="integrationDone && isSuperAdmin" tag="SUPERADMIN">
          <div class="tabs">
            <div class="tab" @click="debug.activeTab = 'orders'" :class="{ active: debug.activeTab === 'orders' }">
              {{ $t('uber_eats_integration.debug_tab_orders') }}
            </div>
            <div class="tab" @click="debug.activeTab = 'notifs'" :class="{ active: debug.activeTab === 'notifs' }">
              {{ $t('uber_eats_integration.debug_tab_notifications') }}
            </div>
            <div class="tab" @click="debug.activeTab = 'requests'" :class="{ active: debug.activeTab === 'requests' }">
              {{ $t('uber_eats_integration.debug_tab_logs') }}
            </div>
          </div>
          <div v-if="debug.activeTab === 'orders'">
            <Datatable ref="order_datatable" entity="external_platform_order"
                       :parameters="{ company: companyId, platform: platformName }"
                       :row-actions="[
                           { text: 'datatable.accept', method: onRowAccept },
                           { text: 'datatable.deny', method: onRowDeny }
                       ]"/>
          </div>
          <div v-if="debug.activeTab === 'notifs'">
            <Datatable entity="uber_eats_notification" :parameters="{ company: companyId }" />
          </div>
          <div v-if="debug.activeTab === 'requests'">
            <Datatable entity="Outgoing_request_log" :parameters="{ company: companyId }" />
          </div>
        </TabSection>
      </Tabs>
    </div>
  </div>
</template>

<script>
  import Empty from "@/components/tools/Empty";
  import PageTitle from "@/components/PageTitle";
  import PanelCard from "@/components/PanelCard.vue";
  import Datatable from "@/components/Datatable.vue";
  import LocalizedInput from "@/components/tools/LocalizedInput.vue";
  import HolidayHours from "@/components/branch/HolidayHours.vue";
  import JsonViewer from "vue-json-viewer";
  import ItemPropertyEdit from "@/components/inventory/ItemPropertyEdit.vue";
  import DayTimeRange from "@/components/tools/DayTimeRange.vue";
  import {mapActions} from "vuex";
  import {Core} from "@/core/Core";
  import Vue from "vue";
  import InformationBlock from "@/components/tools/InformationBlock.vue";

  export default {
    name: "UberEats",

    components: { PageTitle, PanelCard, Datatable, LocalizedInput, HolidayHours, JsonViewer, DayTimeRange,
      InformationBlock, Empty, ItemPropertyEdit },

    data() {
      return {
        //Constants
        augment: 'external_platform',
        platformName: 'uber_eats',

        //Company
        company: null,
        companyCatalogs: [],
        companyItems: [],
        locales: ['en', 'fr'],

        //Integration/Debug
        validStoreID: null,
        uberStoreData: null,
        uberStoreStatusData: {
          status: 'NOT LOADED',
          offlineReason: null,
          paused_until: null
        },
        uberPosData: null,
        uberMenusData: null,
        ishopMenusData: null,
        uberHolidaysData: null,
        integrationState: {
          isExistingUser: null,
          authorizationLinks: {
            localhost: null,
            oauth: null,
          },
          authorizationCode: null,
          companies: {
            all: null, /* lazy loaded on isExistingUser = false */
            selected: null
          },
          user: null,
          userStores: {
            all: [], /* loaded on integrationState.companies.selected */
            selected: null
          }
        },
        storeStatusSetter: {
          status: 'ONLINE',
          reason: undefined
        },
        debug: {
          activeTab: 'orders'
        },

        //Custom hours
        isEditingCustomHours: false,
        emptyHours: {
          monday: {hours: []},
          tuesday: {hours: []},
          wednesday: {hours: []},
          thursday: {hours: []},
          friday: {hours: []},
          saturday: {hours: []},
          sunday: {hours: []},
        },
        editedCustomHours: null,

        //Holiday hours
        holidayHours: [], /* [{date: string, hours: [{from: string, to: string}]}]*/
        menus: [], /* [{catalog: string, hours: string, name: {fr,en,...}}] */
        settings: {
          user: null,
          store_id: null,
          only_export_locales: ['en']
        },

        //Menu
        requirements: {
          alcohol: {
            countries: ['CA', 'US', 'UK', 'FR', 'BE', 'DE', 'MX'],
            items: []
          }
        },
        emptyMenu: {
          catalog: null,
          customHours: {
            monday: {hours: []},
            tuesday: {hours: []},
            wednesday: {hours: []},
            thursday: {hours: []},
            friday: {hours: []},
            saturday: {hours: []},
            sunday: {hours: []},
          },
          hours: null,
          name: { fr: "", en: ""}
        }
      }
    },

    computed: {
      ready() {
        return !!this.company;
      },
      isMobile() {
        return this.$mq === 'xs' || this.$mq === 'sm' || this.$mq === 'md' || this.$mq === 'lg';
      },
      authorizationLinksLoaded() {
        return this.integrationState.authorizationLinks.oauth
            || this.integrationState.authorizationLinks.localhost
      },
      branchId() {
        return this.$route.query.branch;
      },
      companyId() {
        return this.$route.query.company;
      },
      inventoryId() {
        if (!this.company || !this.company.inventory) {
          return null;
        }
        return typeof this.company.inventory === 'number' ? this.company.inventory : this.company.inventory.id;
      },
      integrationDone() {
        return !!this.settings.store_id;
      },
      currentIntegrationStep() {
        let step = null;
        if (this.integrationState.user) {
          step = 'chooseStore';
        } else if (this.integrationState.isExistingUser === true && this.integrationState.companies.selected) {
          step = 'initial.yes.chosen';
        } else if (this.integrationState.isExistingUser === true) {
          step = 'initial.yes';
        } else if (this.integrationState.isExistingUser === false) {
          step = 'initial.no';
        } else {
          step = 'initial';
        }
        return step;
      },
      companyDataToCompare() {
        if (!this.company) { return null; }
        return {
          name: this.company.name,
          location: this.company.address
        }
      },
      uberStoreDataToCompare() {
        let storeData = this.integrationState.userStores.selected;
        if (!storeData) { return null; }
        return {
          name: storeData.name,
          location: storeData.location,
          web_url: storeData.web_url
        }
      },
      country() {
        return this.company ? (this.company.country || '').toUpperCase() : null;
      },
      catalogSelection() {
        return this.companyCatalogs.map(c => ({id: c.type, name: this.$t(`catalog.${c.type}`)}));
      },
      hoursSelection() {
        let methods = ['delivery', 'takeout', 'hotel', 'inPlace'];
        return methods
            .filter(m => !!this.getHoursOfMethod(m))
            .map(m => ({id: m, name: this.$t(`deliveryMethod.${m}`)}))
            .concat([{id: 'custom', name: 'CUSTOM'}])
      },
      menu() {
        //return the first menu of the array or a default one if empty.
        if (!this.menus || this.menus.length < 1) {
          return this.emptyMenu;
        }
        return this.menus[0];
      },
      menuHours() {
        return this.isCustomHours ? this.menu.customHours : this.getHoursOfMethod(this.menu.hours)
      },
      webUrl() {
        return this.uberStoreData ? this.uberStoreData.web_url : "";
      },
      isCustomHours() {
        return this.menu.hours === 'custom';
      },
      getFlatProductsForAlcoholContent() {
        return this.requirements.alcohol.items.map(i => ({...i, alcohol_by_volume: i.moreData ? i.moreData.alcohol_by_volume : null}));
      },
      firstHalfDaysOfTheWeek() {
        return ['monday', 'tuesday', 'wednesday', 'thursday'];
      },
      secondHalfDaysOfTheWeek() {
        return ['friday', 'saturday', 'sunday'];
      },
      isSuperAdmin() {
        return this.$store.getters.loggedUserHasSuperAdmin;
      },
      formattedStoreData() {
        if (!this.uberStoreData) {
          return []
        }
        return [
          { label: this.$t('uber_eats_integration.store_name'), value: this.uberStoreData.name },
          { label: this.$t('uber_eats_integration.price_range'), value: this.uberStoreData.price_bucket },
          { label: this.$t('uber_eats_integration.avg_prep_time'), value: this.uberStoreData.avg_prep_time },
          { label: this.$t('uber_eats_integration.status'), value: this.$t(this.uberStoreData.status), status: this.uberStoreData.status },
          { label: this.$t('uber_eats_integration.timezone'), value: this.uberStoreData.timezone },
          { label: this.$t('uber_eats_integration.integrated_pos_data'), value: (this.uberStoreData.pos_data && this.uberStoreData.pos_data.integration_enabled) ? this.$t('yes') : this.$t('no') },
        ];
      },
      formattedStoreLocation() {
        if (!this.uberStoreData || !this.uberStoreData.location) {
          return []
        }
        return [
          { label: this.$t('address.address'), value: this.uberStoreData.location.address },
          { label: this.$t('address.address') + " 2", value: this.uberStoreData.location.address_2 },
          { label: this.$t('address.city'), value: this.uberStoreData.location.city },
          { label: this.$t('address.state'), value: this.uberStoreData.location.state },
          { label: this.$t('address.country'), value: this.uberStoreData.location.country },
          { label: this.$t('address.zip'), value: this.uberStoreData.location.postal_code },
          { label: this.$t('address.longitude'), value: this.uberStoreData.location.longitude },
          { label: this.$t('address.latitude'), value: this.uberStoreData.location.latitude },
        ];
      },
      formattedPosData() {
        if (!this.uberPosData) {
          return []
        }
        let baseFormattedData = [
          { label: this.$t('uber_eats_integration.store_id'), value: this.uberPosData.store_id },
          { label: this.$t('uber_eats_integration.pos_integration'), value: this.uberPosData.pos_integration_enabled ? this.$t("enabled") : this.$t("disabled") , status: this.uberPosData.pos_integration_enabled },
          { label: this.$t('uber_eats_integration.status'), value: this.$t(this.uberPosData.online_status), status: this.uberPosData.online_status },
          { label: this.$t('uber_eats_integration.integration'), value: this.uberPosData.integration_enabled ? this.$t("enabled") : this.$t("disabled"), status: this.uberPosData.integration_enabled }
        ];
        if (this.isSuperAdmin) {
          return [
            ...baseFormattedData,
            { label: this.$t('uber_eats_integration.order_release'), value: this.uberPosData.order_release_enabled ? this.$t("enabled") : this.$t("disabled"), status: this.uberPosData.order_release_enabled },
            { label: this.$t('uber_eats_integration.auto_accept'), value: this.uberPosData.auto_accept_enabled ? this.$t("enabled") : this.$t("disabled"), status: this.uberPosData.auto_accept_enabled },
          ];
        }
        return baseFormattedData;
      },
      formattedUberStoreStatusData() {
        if (!this.uberStoreStatusData) {
          return []
        }
        return [
          { label: this.$t('uber_eats_integration.status'), value: this.uberStoreStatusData.status },
          { label: this.$t('uber_eats_integration.reason'), value: this.uberStoreStatusData.offlineReason },
        ];
      },
      hasStoreStatusChangedLocally() {
        if (!this.uberStoreStatusData || !this.storeStatusSetter || this.uberStoreStatusData.status === "NOT LOADED") {
          return false;
        }
        return this.uberStoreStatusData.status !== this.storeStatusSetter.status;
      }
    },

    watch: {
      "settings.only_export_locales"(val) {
        /* cannot remove every locale */
        if (val.length === 0) {
          this.$nextTick(() => {
            this.settings.only_export_locales = ['en'];
          })
        }
      },
      "integrationState.isExistingUser": {
        async handler(val) {
          if (val === true && !this.integrationState.companies.all) {
            let response = await this.requestWrap('GET', `/api/uber_eats_integrated_companies`);
            if (response) {
              /* Add labels */
              for (let r of response.companies) {
                r.label = `${r.branchName} - ${r.companyName}`;
              }
              /* Sort by companies with same branch first, sort by labels otherwise */
              let thisBranchId = parseInt(this.branchId);
              response.companies.sort((a, b) => {
                let aIsThisBranch = parseInt(a.branchId) === thisBranchId;
                let bIsThisBranch = parseInt(b.branchId) === thisBranchId;
                if (aIsThisBranch && bIsThisBranch || !aIsThisBranch && !bIsThisBranch) {
                  return a.label.localeCompare(b.label);
                } else if (aIsThisBranch) {
                  return -1;
                } else /* bIsThisBranch */ {
                  return 1;
                }
              });
              this.integrationState.companies.all = response.companies;
            }
          }
          if (val === false) {
            //compatibility for switching from existing user to not, revert to default values
            this.integrationState.companies.selected = null;
            this.integrationState.userStores.selected = null;
            this.integrationState.userStores.all = [];
            this.integrationState.user = null;
            if (!this.authorizationLinksLoaded) {
              let response = await this.requestWrap('GET', `/api/uber_eats_authorization_link/${this.companyId}`);
              if (response) {
                this.integrationState.authorizationLinks.oauth = response.oauth;
                this.integrationState.authorizationLinks.localhost = response.localhost;
              }
            }
          }
        }
      },
      "integrationState.companies.selected"(val) {
        if (val && val.user) {
          this.integrationState.user = val.user;
        }
      },
      "integrationState.user"(val) {
        if (val) {
          this.loadStoresOfUser(val);
        }
      },
      "menu.hours"(val) {
        if (val) {
          if (val === 'custom') {
            this.isEditingCustomHours = true;
          } else if (this.isEditingCustomHours) {
            this.isEditingCustomHours = false;
          }
        }
      }
    },

    async mounted() {
      this.$store.commit('showSpinner', true);
      this.$store.state.pageTitleTranslationKey = "UberEats";
      this.$store.state.pageIcon = "/icon/uber-eats.svg";

      if (!this.companyId) {
        return;//TODO maybe redirect here? if no company we cant do anything.
      }
      await this.loadCompanyAndUberConfig();
      await this.loadHolidayHours();
    },

    methods: {
      ...mapActions(["getCompany", "saveCompany", "getCatalogs", "getHolidayHours", "saveHolidayHours", "getItems"]),

      /* utils */
      async requestWrap(method, url, data) {
        showSpinner();
        let response = await Core.noThrowRequest(method, url, data);
        hideSpinner();
        return response;
      },
      reset() {
        this.integrationState.isExistingUser = false;
        this.restartIntegration(true);
      },
      getHoursOfMethod(method) {
        return method && this.company && this.company.onlineOrdering && this.company.onlineOrdering[method]
            ? this.company.onlineOrdering[method].hours
            : null;
      },
      async loadCompanyAndUberConfig() {
        showSpinner();
        let companyResponse = await this.getCompany({companyId: this.companyId, branchId: this.branchId, augment: this.augment});
        hideSpinner();

        if (companyResponse) {
          let previousConfig = companyResponse.uberEats ? JSON.parse(JSON.stringify(companyResponse.uberEats)) : null;
          if (previousConfig) {
            /* set menus, fix formatting and remove from settings */
            this.menus = previousConfig.menus || [Object.assign({}, this.emptyMenu)];
            delete previousConfig.menus;
            for (let m of this.menus) {
              m.name = Array.isArray(m.name) ? {} : m.name;
            }

            if (this.menu.customHours && !Array.isArray(this.menu.customHours)) {
              this.editedCustomHours = JSON.parse(JSON.stringify(this.menu.customHours));
            } else {
              this.editedCustomHours = Object.assign({}, this.emptyHours)
            }

            /* set settings */
            Object.assign(this.settings, previousConfig);

            /* Add missing locales */
            for (let locale of this.settings.only_export_locales) {
              if (!this.locales.includes(locale)) {
                this.locales.push(locale);
              }
            }

            /* get Uber store and pos data */
            await this.validateConfig();
            await this.loadStatuses();

            /* set integration data when not complete */
            if (this.settings.user && !this.settings.store_id) {
              this.integrationState.user = this.settings.user;
            }
          } else if (this.isIntegrationStepVisible("initial")) {
            this.company = companyResponse;
            this.reset();
          }
          this.company = companyResponse;
          await this.loadCatalogs();
          await this.loadCompanyItems();
        }
      },
      async loadCatalogs() {
        if (!this.company) {
          return;
        }
        showSpinner();
        let response = await this.getCatalogs({inventoryId: this.inventoryId});
        hideSpinner();
        if (response) {
          this.companyCatalogs = response;
        }
      },
      async loadHolidayHours() {
        showSpinner();
        let holidayHoursResponse = await this.getHolidayHours({ companyId: this.companyId })
        hideSpinner();
        if (holidayHoursResponse && holidayHoursResponse.holidayHours && holidayHoursResponse.holidayHours) {
          /* fix formatting */
          for (let hh of holidayHoursResponse.holidayHours) {
            hh.date = hh.date + 'T00:00:00.000-04:00';
          }
          this.holidayHours = holidayHoursResponse.holidayHours
        } else {
          let uberHolidayHoursResponse = await this.requestWrap('GET', `/api/external_platforms/${this.platformName}/holiday_hours`, { company: this.companyId });
          if (!uberHolidayHoursResponse || Util.isObjectEmpty(uberHolidayHoursResponse)) {
            return;
          }

          let holidayHours = [];
          for (let [date, hours] of uberHolidayHoursResponse.holidayHours) {
            let holiday = { date: date + 'T00:00:00.000-04:00', hours: [] }
            for (let hour of hours) {
              if (hour.start_time !== "00:00" && hour.end_time !== "00:00") {
                let holidayHour = { to: hour.start_time, from: hour.end_time }
                holiday.hours.push(holidayHour);
              }
            }
            holidayHours.push(holiday);
          }
          this.holidayHours = holidayHours;
        }
      },

      /* integration related */
      isIntegrationStepVisible(step) {
        return this.currentIntegrationStep.includes(step);
      },
      async consumeAuthorizationCode() {
        let payload = {code: this.integrationState.authorizationCode};
        let response = await this.requestWrap('POST', `/api/uber_eats_consume_code/${this.companyId}`, payload);
        if (response && response.user) {
          this.integrationState.authorizationCode = null;
          this.integrationState.user = response.user;
          this.settings.user = response.user;
        }
      },
      async loadStoresOfUser(user) {
        let response = await this.requestWrap('GET', `/api/uber_eats_stores_of_user/${user}`, {addUsage: 1});
        if (response) {
          this.integrationState.userStores.all = response.stores;
        }
      },
      async assignStoreId(storeId) {
        this.settings.store_id = storeId;
        await this.saveCurrentConfig();
      },

      /* config related  */
      async validateConfig() {
        let response = await this.requestWrap('POST', `/api/external_platforms/${this.platformName}/validate_configuration`, this.settings);
        this.uberStoreData = response.response ? response.response : null;
        this.validStoreID = response ? response.valid : response;
        //TODO i dont understand here, i dont think that the fact that the response is valid or not means that the storeId is not valid.
        return this.validStoreID;
      },
      async validateAndSave() {
        let valid = await this.validateConfig();
        if (valid) {
          await this.saveCurrentConfig();
        } else {
          toast(this.$t('uber_eats_integration.errors.config_validation_failed'), 'warning');
        }
      },
      async initializeUberEatsForInventory() {
        let response = await this.requestWrap("POST", "/api/uber_eats_init_company/" + this.companyId);
        if (response) {
          toast(this.$t('uber_eats_integration.menu_initialized'), 'success');
        }
      },
      async restartIntegration(force) {
        if (!this.isSuperAdmin) {
          return;
        }
        if (!force) {
          let response = await showConfirmation(this.$t("warning"), this.$t('uber_eats_integration.restart_integration_question'));
          if (!response) {
            return;
          }
        }
        await this.saveConfig(null);
      },
      async saveCurrentConfig() {
        await this.saveConfig(this.settings);
      },
      async saveConfig(settings) {
        let companyId = this.company.id;
        let branchId = this.company.branch;
        let augment = this.augment;
        showSpinner();
        let response = await this.saveCompany({companyId, branchId, augment, data: { uberEats: settings }});
        hideSpinner();
        if (response) {
          let responseConfig = response.uberEats || {};
          this.settings.user = responseConfig.user || null;
          this.settings.store_id = responseConfig.store_id || null;
          this.settings.only_export_locales = responseConfig.only_export_locales || ['en'];
          toast(this.$t('uber_eats_integration.settings_saved'), 'success');
        }
      },

      /* status related */
      async loadStatuses() {
        if (!this.uberStoreStatusData || this.uberStoreStatusData.status === 'NOT LOADED') {
          await this.refreshStoreStatus();
        }
        if (!this.uberPosData) {
          await this.refreshPosData();
        }
      },
      async refreshStoreStatus() {
        let status = await this.requestWrap('GET', `/api/external_platforms/${this.platformName}/status`, { company: this.companyId });
        if (status) {
          this.uberStoreStatusData = status;
          this.storeStatusSetter.status = this.uberStoreStatusData.status
        }
      },
      async setStoreStatus(statusObj) {
        let payload = Object.assign({company: this.companyId}, statusObj);
        let response = await this.requestWrap('PUT', `/api/external_platforms/${this.platformName}/status`, payload);
        if (response) {
          toast(this.$t('uber_eats_integration.store_status_changed'), 'success');
          await this.refreshStoreStatus();
        }
      },
      async refreshPosData() {
        let response = await this.requestWrap('GET', `/api/external_platforms/${this.platformName}/pos_data`, { company: this.companyId });
        if (response) {
          this.uberPosData = response;
        }
      },
      async setPosIntegrationStatus(status) {
        if (['create', 'enable', 'disable', 'kill'].includes(status)) {
          let confirmation = await showConfirmation(this.$t("warning"),
              status === 'enable' ? this.$t('uber_eats_integration.warning_integration_enable') : this.$t('uber_eats_integration.warning_integration_disable'));
          if (!confirmation) {
            return;
          }
        } else {
          return;
        }
        let response = await this.requestWrap('PUT', `/api/external_platforms/${this.platformName}/pos_data`, {
          company: this.companyId,
          _action: status,
        });
        if (response) {
          toast(this.$t('uber_eats_integration.pos_status_changed'), 'success');
          await this.refreshPosData();
        }
      },

      /* menu related */
      async saveMenuAndAlcohol() {
        let companyId = this.company.id;
        let branchId = this.company.branch;
        let augment = this.augment;
        let validMenuConfig = this.validateMenuConfig()
        if (!validMenuConfig) {
          return;
        }
        if (this.isCustomHours && this.isEditingCustomHours) {
          let validCustomHours = this.validateCustomHours();
          if (!validCustomHours) {
            return;
          }
          this.setCustomHours();
        }
        showSpinner();
        let [alcoholResponse, companyResponse] = await Promise.all([
          this.saveAlcoholContent(),
          this.saveCompany({ companyId, branchId, augment, data: { uberEats: { menus: this.menus } } })
        ]);
        hideSpinner();
        if (alcoholResponse) {
          toast(this.$t('uber_eats_integration.alcohol_saved'), 'success');
        } else {
          toast(this.$t('uber_eats_integration.errors.alcohol_saved'), 'error');
        }
        if (companyResponse) {
          toast(this.$t('uber_eats_integration.menu_config_changed'), 'success');
        } else {
          toast(this.$t('uber_eats_integration.errors.menu_config_changed'), 'error');
        }

        return !!(alcoholResponse && companyResponse);
      },
      validateMenuConfig() {
        if (!this.menu.catalog) {
          toast(this.$t('uber_eats_integration.errors.menu_missing_catalog'), 'error');
          return false;
        }
        if (!this.menu.hours) {
          toast(this.$t('uber_eats_integration.errors.menu_missing_hours'), 'error');
          return false;
        }
        return true;
      },
      async saveAndSyncMenus() {
        let confirmation = await showConfirmation(this.$t("warning"), this.$t('uber_eats_integration.sync_menu_warning'));
        if (!confirmation) {
          return;
        }
        let success = await this.saveMenuAndAlcohol();
        if (!success) {
          return;
        }

        let response = await this.requestWrap('PUT', `/api/external_platforms/${this.platformName}/menus`, { company: this.companyId });
        if (response) {
          this.ishopMenusData= null;
          this.uberMenusData = null;
          toast(this.$t('uber_eats_integration.menu_synced'), 'success');
        } else {
          toast(this.$t('uber_eats_integration.errors.menu_synced'), 'error');
        }
      },
      async fetchUberAndIshopMenus() {
        let [ishopMenuResponse, uberMenuResponse] = await Promise.all([
          this.requestWrap('GET', `/api/external_platforms/${this.platformName}/preview_menus`, { company: this.companyId }),
          this.requestWrap('GET', `/api/external_platforms/${this.platformName}/menus`, { company: this.companyId })
        ]);
        if (ishopMenuResponse && uberMenuResponse) {
          this.ishopMenusData = ishopMenuResponse;
          this.uberMenusData = uberMenuResponse;
        } else {
          toast(this.$t('uber_eats_integration.errors.uber_menu'), 'error');
        }
      },
      setCustomHours() {
        Vue.set(
            this.menu,
            'customHours',
            JSON.parse(JSON.stringify(this.editedCustomHours))
        );
      },
      validateCustomHours() {
        for (let [day ,customHourRange] of Object.entries(this.editedCustomHours)) {
          for (let hour of customHourRange.hours) {
            if (!hour.from || !hour.to) {
              toast(this.$t('uber_eats_integration.errors.custom_hours_missing_start_end').replace("{DAY}", this.$t(day)), 'error');
              return false;
            }
            if (hour.from >= hour.to) {
              toast(this.$t('uber_eats_integration.errors.custom_hours_bad_range').replace("{DAY}", this.$t(day)), 'error');
              return false;
            }

            let [toHours, toMinutes] = hour.to.split(":").map(t => parseInt(t));
            let [fromHours, fromMinutes] = hour.from.split(":").map(t => parseInt(t));
            let hoursDiff = toHours - fromHours;
            let minutesDiff = toMinutes - fromMinutes;
            if (hoursDiff < 1 || (hoursDiff === 1 && minutesDiff < 0)) {
              toast(this.$t('uber_eats_integration.errors.custom_hours_not_in_range').replace("{DAY}", this.$t(day)), 'error');
              return false;
            }
          }
        }
        return true;
      },

      /* alcohol content related */
      async loadCompanyItems() {
        let options = {inventory: this.inventoryId, add: 'moreData' };
        let allItems = await this.requestWrap('GET', '/api/entity/item_plus_variant/select', options);
        if (allItems && allItems.length > 0) {
          this.companyItems = allItems;
          this.requirements.alcohol.items = allItems.filter(i => i.moreData && i.moreData.alcohol_by_volume >= 0);
        }
      },
      async saveAlcoholContent() {
        /* snapshot data and only keep relevant fields to prevent eventual problems */
        let payload = JSON.parse(JSON.stringify(this.requirements.alcohol.items))
            .map(({id, moreData}) => ({id, moreData}));

        return await this.requestWrap('PUT', `/api/inventory/${this.inventoryId}/item_mass_update`, {payload});
      },
      updateAlcoholContentArray(parameter) {
        if (!parameter.product) { //Item removal
          this.requirements.alcohol.items.splice(parameter.index, 1);
          return;
        }
        let product = this.companyItems.find(i => i.id == parameter.product.id)
        if (!product) {
          return;
        }
        product = Object.assign({}, product);
        if (!product.moreData) { //Adjust item to make sure it fits expected model
          product.moreData = { alcohol_by_volume: null }
        }
        product.moreData.alcohol_by_volume = parameter.product.alcohol_by_volume;
        if (this.requirements.alcohol.items.length <= parameter.index ) { //Item addition
          this.requirements.alcohol.items.push(product)
          return;
        }
        this.requirements.alcohol.items.splice(parameter.index, 1, product);//item replacement
      },

      /* holidays related */
      async saveHolidays() {
        let valid = this.validateHolidayHours();
        if (!valid) {
          return;
        }
        showSpinner();
        let response = await this.saveHolidayHours({companyId: this.company.id, data: this.holidayHours});
        hideSpinner();
        if (response) {
          toast(this.$t('uber_eats_integration.holiday_hours_saved'), 'success')
        } else {
          toast(this.$t('uber_eats_integration.errors.holiday_hours_saved'), 'error');
        }
        return !!response;
      },
      async saveAndSyncHolidays() {
        let confirmation = await showConfirmation(this.$t("warning"), this.$t('uber_eats_integration.sync_holiday_warning'));
        if (!confirmation) {
          return;
        }

        let success = await this.saveHolidays();
        if (!success) {
          return;
        }

        let response = await this.requestWrap('PUT', `/api/external_platforms/${this.platformName}/holiday_hours`, {company: this.companyId});
        if (response) {
          this.uberHolidaysData = null;
          toast(this.$t('uber_eats_integration.holiday_hours_synced'), 'success');
        } else {
          toast(this.$t('uber_eats_integration.errors.holiday_hours_synced'), 'error');
        }
      },
      validateHolidayHours() {
        for (let [i, holiday] of Object.entries(this.holidayHours)) {
          let index = parseInt(i) + 1;
          if (!holiday.date) {
            toast(this.$t('uber_eats_integration.errors.holiday_hours_missing_date').replace("{HOLIDAY_INDEX}", index), 'error');
            return false;
          }
          for (let hour of holiday.hours) {
            if (!hour.from || !hour.to) {
              toast(this.$t('uber_eats_integration.errors.holiday_hours_missing_start_end').replace("{HOLIDAY_INDEX}", index), 'error');
              return false;
            }
            if (hour.to <= hour.from) {
              toast(this.$t('uber_eats_integration.errors.holiday_hours_bad_range').replace("{HOLIDAY_INDEX}", index), 'error');
              return false;
            }
          }
        }
        return true;
      },
      async fetchUberHolidays() {
        let response = await this.requestWrap('GET', `/api/external_platforms/${this.platformName}/holiday_hours`, {company: this.companyId});
        if (response) {
          this.uberHolidaysData = response;
        } else {
          toast(this.$t('uber_eats_integration.errors.uber_holidays'), 'error');
        }
      },

      /* debug related */
      onRowAccept(row) {
        this.manualDecision(row.id, true);
      },
      onRowDeny(row) {
        this.manualDecision(row.id, false);
      },
      async manualDecision(id, accept) {
        let parameters = {
          company: this.companyId,
          platform: this.platformName,
          order_id: id,
          accept: accept
        }
        let response = await this.requestWrap('POST', '/api/external_platforms/accept_order', parameters);
        if (response) {
          toast(accept ? this.$t('accepted') : this.$t('denied'), 'success')
        }
        await this.$refs.order_datatable.query();
      }
    }
  }
</script>

<style lang="scss" scoped>
#uberEats {
  .page-container {
    width: 100%;
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }
}
.panel-card:not(:first-of-type) {
  margin-top: 15px;
}
.card-title {
  line-height: 19px;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 2px;
  color: #404040;
}
.bold-txt {
  line-height: 19px;
  font-size: 14px;
  font-weight: bold;
  color: #404040;
}
.discreet-txt {
  line-height: 16px;
  font-size: 14px;
  color: #808080;
}
.buttons {
  display: flex;
  padding: 10px 0;
  gap: 10px
}
.header {
  display: flex;
  flex-direction: column;

  .header__title_superadmin {
    display: flex;
    column-gap: 10px;
    margin-bottom: 2px;

    .tag {
      font-size: 11px;
      padding: 3px 5px;
      line-height: unset;
    }
    .card-title {
      margin-bottom: 0;
    }
  }
}
.card-content {
  display: flex;
  flex-direction: column;

  .card-section {
    display: flex;
    column-gap: 30px;
    row-gap: 10px;
    flex-wrap: wrap;
  }
  .left, .right {
    display: flex;
    flex-direction: column;
    //min-width: calc(50% - 15px);
    flex: 1 1 0
  }
}
.section__title_superadmin {
  display: flex;
  column-gap: 10px;
  margin-bottom: 2px;

  .tag {
    font-size: 11px;
    padding: 3px 5px;
    line-height: unset;
  }
  .card-title {
    margin-bottom: 0;
  }
}
.uber-data-title {
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  &.padding-bottom {
    padding: 0 0 15px;
  }
}

.card-section__json {
  flex-direction: column;
  &.card-section__json_no_wrap {
    flex-direction: row;
    .jsonViewerContainer {
      padding-top: 15px;
      display: flex;
      flex-wrap: wrap;
      flex-grow: 1;
      flex-basis: 0;
      label {
        width: 100%;
      }
    }
    .jsonViewerContainer .jv-container {
      display: flex;
      flex-grow: 1;
      flex-shrink: 0;
      height: 100%;
      flex-basis: 0;
    }
  }
}
.init-step {
  .div-li {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;

    .btn {
      text-decoration: none;
      margin-top: 10px;
      display: flex;
      align-items: center;
      justify-content: center;

      .svgicon {
        margin-left: 10px;
      }
    }

    .auth-code {
      width: 100%;
      margin-top: 10px;
      word-break: break-all;
    }
  }
}
.explanation-bloc {
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
}
.top-separator {
  padding-top: 20px;
  border-top: 1px dashed #dbdbdb;
  margin-top: 20px;
}
.full-width {
  width: 100%;
}
.assign-button-container {
  display: flex;
  justify-content: flex-end;
}
.locale-container {
  display: flex;
  padding-top: 10px;

  .locales {
    padding-right: 20px;
  }
}
.padding-top {
  padding-top: 10px;
}
.padding-bottom {
  padding-bottom: 10px;
}

.section-title {
  margin-bottom: 0;
  color: #FC6650;
}
.section-header {
  padding-bottom: 15px;
}
.status-container .status-option {
  padding-top: 12px;
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}
.status-change {
  padding: 12px 0 12px 32px;
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}
.justify-end {
  justify-content: flex-end;
}
.menu-name {
  margin-bottom: 10px;
}
.no-hours {
  font-size: 14px;
  color: #808080;
  font-style: italic;
  padding: 20px
}
.card-section.menu-sync-actions {
  justify-content: flex-end;
  padding-top: 10px;
  column-gap: 10px;

  .menu-sync-action {
    margin-top: 10px;
  }
}

.btnWeb {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  .svgicon {
    margin-left: 10px;
  }
}

.alcohol-setter {
  display: flex;
  flex-wrap: wrap;

  .alcohol-setter__container {
    width: 100%;
    background: #FFFFFF;
    max-width: calc(50% - 64px);
    padding: 12px;
    box-shadow: 0 1px 2px #0000001F;
    border-radius: 8px;
  }
  .alcohol-setter__add {
    border: 1px dashed #e6e6e6;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #fc6650;
    min-height: 76px;
    cursor: pointer;
    font-size: 14px;

    .svgicon {
      margin-right: 15px;
      fill: #fc6650 !important;
    }
  }
}
#uberEats.mobile {
  .card-content .card-section {
    flex-direction: column;
    &.card-section__json.card-section__json_no_wrap {
      flex-direction: column;
      .jsonViewerContainer .jv-container {
        margin-top: 10px;
      }
    }
  }
}
</style>